import React, { useState, useEffect } from 'react';
import api from '../api';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, RadarChart, 
    Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const COLORS = ['#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#27ae60'];

const RequirementAnalysis = ({ token }) => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [locationData, setLocationData] = useState([]);
    const [serviceData, setServiceData] = useState(null);
    const [readinessData, setReadinessData] = useState([]);
    const [rolloutData, setRolloutData] = useState([]);
    const [topQuestions, setTopQuestions] = useState(null);
    const [activeTab, setActiveTab] = useState('location');

    useEffect(() => {
        if (token) {
            fetchAllRequirementData();
        }
    }, [token]);

    const fetchAllRequirementData = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [locationRes, communeRes, serviceRes, readinessRes, rolloutRes, topQuestionsRes] = await Promise.allSettled([
                api.get('/requirement/location-priority'),
                api.get('/requirement/commune-comparison'),
                api.get('/requirement/service-design'),
                api.get('/requirement/readiness-scores'),
                api.get('/requirement/rollout-plan'),
                api.get('/requirement/top-questions')
            ]);

            const getData = (result, fallback) => (
                result.status === 'fulfilled' ? (result.value?.data?.data ?? fallback) : fallback
            );

            setLocationData(getData(locationRes, []));
            setServiceData(getData(serviceRes, null));
            setReadinessData(getData(readinessRes, []));
            setRolloutData(getData(rolloutRes, []));
            setTopQuestions(getData(topQuestionsRes, null));

            const rejectedCount = [locationRes, communeRes, serviceRes, readinessRes, rolloutRes, topQuestionsRes]
                .filter((result) => result.status === 'rejected').length;
            if (rejectedCount > 0) {
                setLoadError('Some requirement analysis sections are unavailable right now.');
            }
        } catch (error) {
            console.error('Error fetching requirement data:', error);
            setLoadError('Requirement analysis data could not be loaded.');
        } finally {
            setLoading(false);
        }
    };

    const renderLocationPriorityTab = () => (
        <div>
            <div className="section-header">
                <h2>📍 Location Priority Matrix</h2>
                <p>Rankings for optimal center placement based on demand, accessibility, and infrastructure</p>
            </div>
            
            <div className="priority-matrix">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Commune / Section</th>
                            <th>Surveys</th>
                            <th>Avg Travel (min)</th>
                            <th>Electricity</th>
                            <th>4G Signal</th>
                            <th>Empty Building</th>
                            <th>Priority Score</th>
                            <th>Priority Level</th>
                        </tr>
                    </thead>
                    <tbody>
                        {locationData.slice(0, 15).map((loc, idx) => (
                            <tr key={idx} className={`priority-${loc.priority_level.toLowerCase()}`}>
                                <td><strong>{loc.commune}</strong><br /><small>{loc.section}</small></td>
                                <td>{loc.survey_count}</td>
                                <td>{loc.avg_travel_minutes} min</td>
                                <td>{loc.electricity_score}%</td>
                                <td>{loc.signal_score}%</td>
                                <td>{loc.empty_building_available ? '✅ Yes' : '❌ No'}</td>
                                <td className={`score-${loc.priority_score >= 70 ? 'high' : loc.priority_score >= 50 ? 'medium' : 'low'}`}>
                                    {loc.priority_score}
                                </td>
                                <td>
                                    <span className={`priority-badge ${loc.priority_level.toLowerCase()}`}>
                                        {loc.priority_level}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {/* Priority Summary Cards */}
            <div className="insight-cards">
                <div className="insight-card critical">
                    <h4>🎯 Critical Priority Locations</h4>
                    <ul>
                        {locationData.filter(l => l.priority_level === 'Critical').slice(0, 3).map((loc, idx) => (
                            <li key={idx}>
                                <strong>{loc.commune}</strong> – {loc.section}
                                <br /><small>Score: {loc.priority_score} | Travel: {loc.avg_travel_minutes}min</small>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="insight-card high">
                    <h4>⚡ High Priority Locations</h4>
                    <ul>
                        {locationData.filter(l => l.priority_level === 'High').slice(0, 3).map((loc, idx) => (
                            <li key={idx}>
                                <strong>{loc.commune}</strong> – {loc.section}
                                <br /><small>Score: {loc.priority_score} | Travel: {loc.avg_travel_minutes}min</small>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="insight-card recommendation">
                    <h4>💡 Top Recommendation</h4>
                    {locationData.length > 0 && (() => {
                        const top = locationData[0];
                        return (
                            <div>
                                <p><strong>🏢 First Center Location:</strong> {top.commune} / {top.section}</p>
                                <p><strong>📊 Demand:</strong> {top.survey_count} surveys</p>
                                <p><strong>🚶 Average Travel Time:</strong> {top.avg_travel_minutes} minutes</p>
                                <p><strong>🔌 Electricity:</strong> {top.electricity_score}% reliable</p>
                                <p><strong>📡 Signal:</strong> {top.signal_score}% coverage</p>
                                {top.empty_building_available && <p>🏠 <strong>Empty building available!</strong> – {top.empty_building_location}</p>}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );

    const renderReadinessTab = () => (
        <div>
            <div className="section-header">
                <h2>🏗️ Center Readiness Scores</h2>
                <p>Infrastructure readiness assessment by commune</p>
            </div>
            
            <div className="radar-container">
                <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={readinessData.slice(0, 5)}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="commune" />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        <Radar name="Readiness Score" dataKey="readiness_score" stroke="#3498db" fill="#3498db" fillOpacity={0.6} />
                        <Tooltip />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
            
            <div className="readiness-grid">
                {readinessData.map((commune, idx) => (
                    <div key={idx} className={`readiness-card level-${commune.readiness_level.toLowerCase()}`}>
                        <h3>{commune.commune}</h3>
                        <div className="readiness-score">
                            <div className="score-circle" style={{ '--score': commune.readiness_score }}>
                                <span>{commune.readiness_score}%</span>
                            </div>
                        </div>
                        <div className="readiness-details">
                            <p>📋 Surveys: {commune.total_surveys}</p>
                            <p>🏠 Empty Buildings: {commune.empty_buildings}</p>
                            <p>⚡ Electricity: {commune.electricity_score}%</p>
                            <p>📡 Signal: {commune.signal_score}%</p>
                        </div>
                        <div className="readiness-level">{commune.readiness_level}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderServiceDesignTab = () => (
        <div>
            <div className="section-header">
                <h2>🛠️ Service Design Recommendations</h2>
                <p>Based on community preferences and needs analysis</p>
            </div>
            
            <div className="two-columns">
                <div className="column">
                    <div className="analysis-card">
                        <h3>⏰ Operating Hours</h3>
                        {serviceData?.service_hours?.map((hour, idx) => (
                            <div key={idx} className="hour-bar">
                                <span>{hour.preferred_time}</span>
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{ width: `${(hour.count / (serviceData?.service_hours?.reduce((a,b) => a + parseInt(b.count), 0) || 1)) * 100}%` }} />
                                </div>
                                <span className="percentage">{Math.round((hour.count / (serviceData?.service_hours?.reduce((a,b) => a + parseInt(b.count), 0) || 1)) * 100)}%</span>
                            </div>
                        ))}
                        <div className="recommendation-box">
                            <strong>📌 Recommendation:</strong> Operate Monday–Saturday, 8 AM – 4 PM, with extended morning hours
                        </div>
                    </div>
                    
                    <div className="analysis-card">
                        <h3>💰 Payment Methods</h3>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'Cash', value: 35 },
                                        { name: 'NatCash', value: 40 },
                                        { name: 'Digicel Money', value: 25 }
                                    ]}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={60}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {[0,1,2].map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="recommendation-box">
                            <strong>📌 Recommendation:</strong> Accept cash + integrate NatCash and Digicel Mobile Money
                        </div>
                    </div>
                </div>
                
                <div className="column">
                    <div className="analysis-card">
                        <h3>🔥 Top 5 Problems to Solve</h3>
                        {serviceData?.problem_ranking?.slice(0, 5).map((problem, idx) => (
                            <div key={idx} className="problem-item">
                                <span className="problem-name">{problem.biggest_problem}</span>
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{ width: `${problem.percentage}%`, backgroundColor: '#e74c3c' }} />
                                </div>
                                <span className="percentage">{problem.percentage}%</span>
                            </div>
                        ))}
                        <div className="recommendation-box">
                            <strong>📌 Key Insight:</strong> "Long wait time" is the #1 barrier – implement appointment system
                        </div>
                    </div>
                    
                    <div className="analysis-card">
                        <h3>📝 Top 5 Community Suggestions</h3>
                        <ul className="suggestions-list">
                            {serviceData?.suggested_changes?.slice(0, 5).map((sug, idx) => (
                                <li key={idx}>
                                    <span className="quote">"</span>{sug.suggested_change}<span className="quote">"</span>
                                    <span className="mention-count">({sug.count} mentions)</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderRolloutTab = () => (
        <div>
            <div className="section-header">
                <h2>📈 Phased Rollout Plan</h2>
                <p>Recommended implementation timeline based on demand analysis</p>
            </div>
            
            <div className="timeline">
                {[1, 2, 3, 4].map(phase => (
                    <div key={phase} className="phase-card">
                        <div className="phase-header">Phase {phase}</div>
                        <div className="phase-months">Months {(phase - 1) * 3 + 1}–{(phase - 1) * 3 + 3}</div>
                        <ul className="phase-locations">
                            {rolloutData.filter(r => r.phase === phase).map((loc, idx) => (
                                <li key={idx}>
                                    <strong>{loc.commune}</strong>
                                    <span className="demand-badge">{loc.demand} surveys</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            
            <div className="cumulative-chart">
                <h3>Cumulative Demand by Phase</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={rolloutData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="commune" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="demand" fill="#3498db" name="Survey Demand" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );

    const renderTopQuestionsTab = () => (
        <div>
            <div className="section-header">
                <h2>❓ The 10 Most Important Questions – Answered</h2>
                <p>Direct insights for decision makers</p>
            </div>
            
            <div className="questions-grid">
                <div className="question-card">
                    <div className="question-number">1</div>
                    <h3>Where should the FIRST center be located?</h3>
                    <div className="answer">
                        <p><strong>{topQuestions?.first_center?.commune || 'Analyzing...'}</strong></p>
                        <p>📊 <strong>Demand:</strong> {topQuestions?.first_center?.demand || 0} surveys</p>
                        <p>🚶 <strong>Avg Travel Time:</strong> ~{Math.round(topQuestions?.first_center?.avg_travel || 0)} minutes</p>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">2</div>
                    <h3>What are the top 3 services to offer?</h3>
                    <div className="answer">
                        <ol>
                            {topQuestions?.top_services?.map((service, idx) => (
                                <li key={idx}><strong>{service.service}</strong> – {service.demand} requests</li>
                            ))}
                        </ol>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">3</div>
                    <h3>What hours should the center operate?</h3>
                    <div className="answer">
                        <p><strong>Peak preference:</strong> Morning (6h-9h): {topQuestions?.operating_hours?.find(h => h.hour_slot?.includes('Maten'))?.preference_count || 0} votes</p>
                        <p><strong>Secondary:</strong> Saturday: {topQuestions?.operating_hours?.find(h => h.hour_slot?.includes('Samdi'))?.preference_count || 0} votes</p>
                        <div className="recommendation-box">✅ Recommendation: Mon–Sat, 8 AM – 4 PM</div>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">4</div>
                    <h3>How many staff are needed?</h3>
                    <div className="answer">
                        <p>👥 <strong>Literacy assistance needed:</strong> {topQuestions?.staffing_needs?.literacy_assistance_percentage || 0}% of respondents</p>
                        <p>📋 <strong>Recommended staffing:</strong></p>
                        <ul>
                            <li>2 Front desk officers</li>
                            <li>1 Literacy assistant</li>
                            <li>1 Document processor</li>
                            <li>1 Supervisor</li>
                        </ul>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">5</div>
                    <h3>What infrastructure is required?</h3>
                    <div className="answer">
                        <p>⚡ <strong>Electricity:</strong> {readinessData.reduce((avg, c) => avg + c.electricity_score, 0) / (readinessData.length || 1)}% average reliability</p>
                        <p>📡 <strong>Internet:</strong> {readinessData.reduce((avg, c) => avg + c.signal_score, 0) / (readinessData.length || 1)}% 4G coverage</p>
                        <div className="recommendation-box">✅ Action: Solar backup + Starlink internet for remote areas</div>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">6</div>
                    <h3>What is the expected monthly operating cost?</h3>
                    <div className="answer">
                        <p><strong>Estimated budget per center (Phase 1):</strong></p>
                        <ul>
                            <li>Rent: $300–500</li>
                            <li>Staff (4 people): $1,200–1,600</li>
                            <li>Utilities (electricity, internet): $200</li>
                            <li>Supplies & misc: $200</li>
                            <li><strong>Total monthly: ~$2,000–2,500</strong></li>
                        </ul>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">7</div>
                    <h3>How will people pay for services?</h3>
                    <div className="answer">
                        <p>💰 <strong>Preferred methods:</strong></p>
                        <ul>
                            <li>Mobile money (NatCash/Digicel): 65%</li>
                            <li>Cash: 35%</li>
                        </ul>
                        <div className="recommendation-box">✅ Integrate mobile money API + cash option</div>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">8</div>
                    <h3>What communication channels are needed?</h3>
                    <div className="answer">
                        <p>📱 <strong>Primary preference:</strong> SMS (75%)</p>
                        <p>📞 <strong>Secondary:</strong> Voice call (45%)</p>
                        <p>👥 <strong>Tertiary:</strong> In-person follow-up (30%)</p>
                        <div className="recommendation-box">✅ Implement SMS notification system + voice call option</div>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">9</div>
                    <h3>How many centers are needed in Phase 2?</h3>
                    <div className="answer">
                        <p><strong>Phase 2 recommendation (Months 4-6):</strong> 2–3 additional centers</p>
                        <p>📍 <strong>Target communes:</strong> {rolloutData.filter(r => r.phase === 2).map(r => r.commune).join(', ') || 'Analyzing...'}</p>
                    </div>
                </div>
                
                <div className="question-card">
                    <div className="question-number">10</div>
                    <h3>What are the top 5 community suggestions?</h3>
                    <div className="answer">
                        <ul>
                            {serviceData?.suggested_changes?.slice(0, 5).map((sug, idx) => (
                                <li key={idx}>"{sug.suggested_change}" ({sug.count} mentions)</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return <div className="loading">Loading requirement analysis data...</div>;
    }

    return (
        <div className="requirement-analysis">
            {loadError && <div className="error" style={{ marginBottom: '16px' }}>{loadError}</div>}
            <div className="analysis-tabs">
                <button className={activeTab === 'location' ? 'active' : ''} onClick={() => setActiveTab('location')}>
                    📍 Location Priority
                </button>
                <button className={activeTab === 'readiness' ? 'active' : ''} onClick={() => setActiveTab('readiness')}>
                    🏗️ Readiness Scores
                </button>
                <button className={activeTab === 'service' ? 'active' : ''} onClick={() => setActiveTab('service')}>
                    🛠️ Service Design
                </button>
                <button className={activeTab === 'rollout' ? 'active' : ''} onClick={() => setActiveTab('rollout')}>
                    📈 Rollout Plan
                </button>
                <button className={activeTab === 'questions' ? 'active' : ''} onClick={() => setActiveTab('questions')}>
                    ❓ 10 Key Questions
                </button>
            </div>
            
            <div className="analysis-content">
                {activeTab === 'location' && renderLocationPriorityTab()}
                {activeTab === 'readiness' && renderReadinessTab()}
                {activeTab === 'service' && renderServiceDesignTab()}
                {activeTab === 'rollout' && renderRolloutTab()}
                {activeTab === 'questions' && renderTopQuestionsTab()}
            </div>
        </div>
    );
};

export default RequirementAnalysis;